import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1100, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnInfo = { padding: '6px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };

const subjectColors = {
  english: { bg: '#007BFF', text: '#fff' },
  math: { bg: '#28a745', text: '#fff' },
  science: { bg: '#6f42c1', text: '#fff' }
};

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

export default function ReviewExams() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { fetchExams(); }, []);

  const fetchExams = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/review/exams`);
      if (!res.ok) throw new Error('Failed to fetch exam reviews');
      setExams(await res.json());
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  }) : '—';

  const formatDuration = (sec) => {
    if (!sec) return '—';
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${m}m ${s}s`;
  };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Review Exam Results</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={btnSecondary}>← Back</button>
      </div>

      {error && <div style={{ padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>{error}</div>}

      {/* Table Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '2.5fr 1.2fr 1fr 1fr 1.2fr 1.5fr', gap: 10, padding: '10px 15px', background: '#e9ecef', borderRadius: '8px 8px 0 0', fontWeight: 600, fontSize: 14 }}>
        <div>Exam Name</div>
        <div>Subject</div>
        <div>Completed</div>
        <div>Pending</div>
        <div>Avg Score</div>
        <div>Actions</div>
      </div>

      {loading && <p style={{ textAlign: 'center', padding: 20 }}>Loading exam reviews...</p>}
      
      {!loading && exams.length === 0 && (
        <p style={{ textAlign: 'center', padding: 20, color: '#666' }}>
          No exam results to review yet.
        </p>
      )}

      {/* Exam List */}
      {!loading && exams.map(exam => {
        const colors = subjectColors[exam.exam_topic] || { bg: '#6c757d', text: '#fff' };
        const completed = exam.completed_count || 0;
        const pending = exam.pending_count || 0;
        const isDiagnostic = exam.diagnostic_config !== null;
        
        // ✅ Show "N/A" for diagnostic exams since they use classifications instead of scores
        const avgScore = isDiagnostic 
          ? <span style={{ color: '#888', fontStyle: 'italic' }}>Diagnostic</span>
          : exam.avg_score ? `${exam.avg_score}%` : '—';
        const avgTime = exam.avg_duration ? formatDuration(exam.avg_duration) : '—';

        return (
          <div key={exam.id} style={{ display: 'grid', gridTemplateColumns: '2.5fr 1.2fr 1fr 1fr 1.2fr 1.5fr', gap: 10, padding: '12px 15px', background: '#fff', borderBottom: '1px solid #eee', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {exam.exam_name}
                {isDiagnostic && (
                  <span style={{ 
                    marginLeft: 8, 
                    padding: '2px 8px', 
                    background: '#e7f1ff', 
                    color: '#0056b3', 
                    borderRadius: 10, 
                    fontSize: 10, 
                    fontWeight: 600 
                  }}>
                    📊 Diagnostic
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#888' }}>Created: {formatDate(exam.created_at)}</div>
            </div>
            <div>
              <span style={{ 
                padding: '3px 10px', 
                background: colors.bg, 
                color: colors.text, 
                borderRadius: 12, 
                fontSize: 11, 
                fontWeight: 600,
                textTransform: 'capitalize'
              }}>
                {exam.exam_topic}
              </span>
            </div>
            <div style={{ textAlign: 'center', fontWeight: 500, color: '#28a745' }}>{completed}</div>
            <div style={{ textAlign: 'center', color: '#666' }}>{pending}</div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 500 }}>{avgScore}</div>
              <div style={{ fontSize: 11, color: '#888' }}>{avgTime} avg</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button 
                onClick={() => navigate(`/admin/review-exams/${exam.id}`)} 
                style={{ ...btnInfo, padding: '6px 12px', fontSize: 13 }}
                disabled={completed === 0}
              >
                {completed > 0 ? 'View Results' : 'No completions'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}